import AddSenderAccountModal from 'components/domain/AddSenderAccountModal';
import Modal from 'components/ui/Modal';
import {
  Edit,
  Globe,
  Loader2,
  MoreHorizontal,
  Plus,
  Trash,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'store';
import {
  createSenderAccountRequest,
  deleteSenderAccountRequest,
  updateSenderAccountRequest,
} from 'store/senderAccounts';
import AddDomainModal from '../../components/domain/AddDomainModal';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
// import ProviderBadge from '../../components/ui/ProviderBadge';
import { useToast } from '../../contexts/ToastContext';
import {
  createDomainRequest,
  getDomainsRequest,
  verifyDomainRequest,
  clearDomainState,
  deleteDomainRequest,
} from '../../store/domain/domain.action';
import {
  selectDomains,
  selectDomainsError,
  selectDomainVerificationLoading,
} from '../../store/domain/domain.selector';
import { IDomain, ISenderAccount } from '../../types';

export interface DomainData {
  domain: string;
  displayName: string;
  emailAddress: string;
  provider?: 'aws' | 'mailgun';
}

interface DomainCardProps {
  domain: IDomain;
}

const DomainSetup: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch() as any;
  const { showToast } = useToast();
  const domains = useSelector(selectDomains) as IDomain[];
  const error = useSelector(selectDomainsError) as string | null;
  const verificationLoading = useSelector(selectDomainVerificationLoading);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isAddSenderAccountModalOpen, setIsAddSenderAccountModalOpen] =
    useState<boolean>(false);
  const [selectedDomain, setSelectedDomain] = useState<IDomain | null>(null);
  const [selectedSenderAccount, setSelectedSenderAccount] =
    useState<ISenderAccount | null>(null);
  const [isDeleteSenderAccountModalOpen, setIsDeleteSenderAccountModalOpen] =
    useState<boolean>(false);
  const [moreOptionsDomainId, setMoreOptionsDomainId] = useState<string | null>(
    null
  );
  const [isDeleteDomainModalOpen, setIsDeleteDomainModalOpen] =
    useState<boolean>(false);
  const [domainToDelete, setDomainToDelete] = useState<IDomain | null>(null);
  const isLoading = useSelector(
    (state: RootState) => state.senderAccounts.loading
  );

  useEffect(() => {
    dispatch(getDomainsRequest());
  }, [dispatch]);

  // Show toast notification when there's an error
  useEffect(() => {
    if (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: error,
      });
      dispatch(clearDomainState());
    }
  }, [error, showToast, dispatch]);

  const handleAddDomain = (domainData: DomainData): void => {
    const provider = domainData.provider || 'aws'; // Default to AWS if not specified

    const domainPayload: any = {
      domain: domainData.domain,
      subDomain: `sender.${domainData.domain}`,
      displayName: domainData.displayName,
      email: domainData.emailAddress,
    };

    dispatch(createDomainRequest(domainPayload, provider));
  };

  const handleAddSenderAccount = (senderAccountData: {
    displayName: string;
    emailAddress: string;
    providerType: 'aws' | 'mailgun';
  }): void => {
    if (selectedSenderAccount && selectedSenderAccount._id) {
      dispatch(
        updateSenderAccountRequest({
          id: selectedSenderAccount._id,
          data: {
            name: senderAccountData.displayName,
            email: senderAccountData.emailAddress,
            domainId: selectedDomain?._id || '',
            providerType: senderAccountData.providerType,
          },
        })
      );
    } else {
      dispatch(
        createSenderAccountRequest({
          name: senderAccountData.displayName,
          email: senderAccountData.emailAddress,
          domainId: selectedDomain?._id || '',
          providerType: senderAccountData.providerType,
        })
      );
    }
  };

  const handleShowRecords = (domain: IDomain): void => {
    navigate(`/domain-setup/${domain._id}/records`);
  };
  const handleDelete = (): void => {
    dispatch(deleteSenderAccountRequest(selectedSenderAccount?._id || ''));
    setIsDeleteSenderAccountModalOpen(false);
    setSelectedDomain(null);
    setSelectedSenderAccount(null);
  };

  const handleDeleteDomain = (): void => {
    if (domainToDelete?._id) {
      const provider = domainToDelete.type || 'aws';
      dispatch(deleteDomainRequest(domainToDelete._id, provider) as any);
    }
    setIsDeleteDomainModalOpen(false);
    setDomainToDelete(null);
  };

  const DeleteSenderAccountModal = ({
    open,
    onClose,
    onDelete,
  }: {
    open: boolean;
    onClose: () => void;
    onDelete: () => void;
  }) => (
    <Modal isOpen={open} onClose={onClose} title="Delete Sender Account">
      <p className="text-muted-foreground mb-6">
        Are you sure you want to delete this sender account? This action cannot
        be undone.
      </p>
      <div className="flex justify-end gap-3 pt-4 border-t border-border">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onDelete}>
          Delete
        </Button>
      </div>
    </Modal>
  );

  const DeleteDomainModal = ({
    open,
    onClose,
    onDelete,
  }: {
    open: boolean;
    onClose: () => void;
    onDelete: () => void;
  }) => (
    <Modal isOpen={open} onClose={onClose} title="Delete domain">
      <p className="text-muted-foreground mb-6">
        Are you sure you want to delete this domain? This action cannot be
        undone.
      </p>
      <div className="flex justify-end gap-3 pt-4 border-t border-border">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onDelete}>
          Delete
        </Button>
      </div>
    </Modal>
  );

  const DomainCard: React.FC<DomainCardProps> = ({ domain }) => {
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (moreOptionsDomainId !== domain._id) return;
      const handleClickOutside = (e: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(e.target as Node)
        ) {
          setMoreOptionsDomainId(null);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- need to react when this card's dropdown open state changes
    }, [moreOptionsDomainId, domain._id]);

    const isDropdownOpen = moreOptionsDomainId === domain._id;

    return (
      <div className="bg-card border border-border rounded-xl p-6 space-y-5 shadow-sm">
        {/* Header row */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted">
                <Globe className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">
                  {domain.displayName}
                </h3>
                <p className="text-sm text-muted-foreground">{domain.domain}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant={domain.status === 'verified' ? 'verified' : 'pending'}
                size="small"
              >
                {domain.status === 'verified' ? 'Verified' : 'Unverified'}
              </Badge>
              {/* {domain.type && (
                <ProviderBadge
                  provider={domain.type}
                  size="small"
                  variant="outline"
                  showTooltip={true}
                />
              )} */}
              <span
                className={`w-2 h-2 rounded-full shrink-0 ${
                  domain.status === 'verified' ? 'bg-success' : 'bg-destructive'
                }`}
                aria-hidden
              />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="small"
              disabled={domain.status !== 'verified'}
              onClick={() => {
                setSelectedDomain(domain);
                setIsAddSenderAccountModalOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Add from address
            </Button>
            <Button
              variant="outline"
              size="small"
              onClick={() => handleShowRecords(domain)}
            >
              Show Records
            </Button>
            {domain.status === 'pending' && (
              <Button
                variant="primary"
                size="small"
                disabled={verificationLoading}
                onClick={() => {
                  const provider = domain.type || 'aws';
                  dispatch(
                    verifyDomainRequest(
                      domain._id || '',
                      'domainSetup',
                      provider
                    ) as any
                  );
                }}
              >
                {verificationLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                    Verifying…
                  </>
                ) : (
                  'Verify Domain'
                )}
              </Button>
            )}
            <div className="relative inline-block" ref={dropdownRef}>
              <Button
                variant="ghost"
                size="small"
                aria-label="More options"
                aria-expanded={isDropdownOpen}
                aria-haspopup="true"
                onClick={() =>
                  setMoreOptionsDomainId(
                    isDropdownOpen ? null : (domain._id ?? null)
                  )
                }
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
              {isDropdownOpen && (
                <div
                  className="absolute right-0 top-full mt-1 min-w-[160px] rounded-lg border border-border bg-card py-1 shadow-lg z-50"
                  role="menu"
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 focus:bg-destructive/10 focus:outline-none"
                    onClick={() => {
                      setDomainToDelete(domain);
                      setMoreOptionsDomainId(null);
                      setIsDeleteDomainModalOpen(true);
                    }}
                  >
                    <Trash className="w-4 h-4 shrink-0" />
                    Delete domain
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Domain details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
              Link tracking
            </h4>
            <p className="text-sm text-foreground">{domain.subDomain}</p>
          </div>
          {/* <div>
            <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
              Mail server
            </h4>
            <p className="text-sm text-foreground">Shared IP Pool – Default</p>
          </div> */}
        </div>

        {domain.status === 'verified' && domain.senderAccounts?.length > 0 && (
          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-medium text-foreground mb-3">
              Dynamic from addresses
            </h4>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide w-12">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide min-w-[140px]">
                      Sender name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide min-w-[180px]">
                      Sender address
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wide w-24">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-8 text-center text-muted-foreground"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
                          Loading…
                        </div>
                      </td>
                    </tr>
                  ) : (
                    domain.senderAccounts?.map(
                      (address: ISenderAccount, index: number) => (
                        <tr
                          key={address._id ?? index}
                          className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                        >
                          <td className="px-4 py-3 text-sm text-foreground">
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 text-sm text-foreground">
                            {address.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-foreground">
                            {address.email}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="small"
                                onClick={() => {
                                  setSelectedSenderAccount(address);
                                  setSelectedDomain(domain);
                                  setIsAddSenderAccountModalOpen(true);
                                }}
                                aria-label="Edit sender"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="small"
                                disabled={domain.senderAccounts?.length === 1}
                                onClick={() => {
                                  setSelectedSenderAccount(address);
                                  setIsDeleteSenderAccountModalOpen(true);
                                }}
                                aria-label="Delete sender"
                                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                              >
                                <Trash className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6 bg-background">
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-foreground">
            Domain setup
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage sending domains and from addresses for your campaigns
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center gap-2 shrink-0"
        >
          <Plus className="w-4 h-4" />
          Add sending domain
        </Button>
      </div>

      {domains?.length === 0 ? (
        <div className="bg-card rounded-xl border border-border">
          <div className="flex flex-col items-center justify-center py-20 px-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-card-foreground mb-2">
                No domains yet
              </h3>
              <p className="text-muted-foreground mb-8 max-w-md">
                Add your first sending domain to verify DNS and start sending
                from your own addresses.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="primary"
                  onClick={() => setIsAddModalOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add sending domain
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {domains?.map((domain) => (
            <DomainCard key={domain._id} domain={domain} />
          ))}
        </div>
      )}

      <AddDomainModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedDomain(null);
          setSelectedSenderAccount(null);
        }}
        onSubmit={handleAddDomain}
      />
      <AddSenderAccountModal
        isOpen={isAddSenderAccountModalOpen}
        onClose={() => {
          setIsAddSenderAccountModalOpen(false);
          setSelectedDomain(null);
          setSelectedSenderAccount(null);
        }}
        onSubmit={handleAddSenderAccount}
        domain={selectedDomain?.domain}
        senderAccount={selectedSenderAccount || undefined}
        providerType={selectedDomain?.type || 'aws'}
      />
      <DeleteSenderAccountModal
        open={isDeleteSenderAccountModalOpen}
        onClose={() => {
          setIsDeleteSenderAccountModalOpen(false);
          setSelectedDomain(null);
          setSelectedSenderAccount(null);
        }}
        onDelete={handleDelete}
      />
      <DeleteDomainModal
        open={isDeleteDomainModalOpen}
        onClose={() => {
          setIsDeleteDomainModalOpen(false);
          setDomainToDelete(null);
        }}
        onDelete={handleDeleteDomain}
      />
    </div>
  );
};

export default DomainSetup;
